import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';

export interface SideBarMenuItem {
  id: string;
  title: string;
  order: number;
  icon: string;
  path: string;
  isSubMenuOpen?: boolean;
  isPocOnly?: boolean;
  isSiteProtectedFeature?: boolean;
  subMenuItems: SideBarSubMenuItem[];
}

export interface SideBarSubMenuItem {
  id: string;
  title: string;
  order: number;
  icon?: string;
  path: string;
  isPocOnly?: boolean;
  isSiteProtectedFeature?: boolean;
}

const whiteListedFeatures = [
  {
    siteId: '641d914945eb2',
    activatedProtectedFeatureIds: ['mmm']
  },
  {
    siteId: 'demo',
    activatedProtectedFeatureIds: ['mmm']
  }
] as { siteId: string; activatedProtectedFeatureIds: string[] }[];

const initialState = {
  isSidebarOpen: true,
  sideBarMenuItems: [
    {
      id: 'dashboard',
      title: 'Dashboard',
      order: 1,
      icon: 'ra-icons-generic-menu',
      path: 'dashboard',
      subMenuItems: []
    },
    {
      id: 'reports',
      title: 'Reports',
      order: 2,
      icon: 'ra-icons-generic-page-1',
      subMenuItems: [
        {
          id: 'advanced-reports',
          title: 'Advanced Reports',
          icon: 'ra-icons-marketing-arrow-chart-1',
          path: 'advanced-reports',
          order: 1
        },
        {
          id: 'reports-source',
          title: 'Source',
          path: 'reports/source',
          order: 2
        },
        {
          id: 'reports-keyword',
          title: 'Keyword',
          path: 'reports/keywords',
          order: 3
        },
        {
          id: 'reports-landing-page',
          title: 'Landing Page',
          path: 'reports/landing-page',
          order: 4
        },
        {
          id: 'reports-referrer',
          title: 'Referrer',
          path: 'reports/referrer',
          order: 5
        },
        {
          id: 'reports-utm-campaign',
          title: 'UTM Campaign',
          path: 'reports/utm-campaign',
          order: 6
        },
        {
          id: 'reports-utm-medium',
          title: 'UTM Medium',
          path: 'reports/utm-medium',
          order: 7
        },
        {
          id: 'reports-utm-content',
          title: 'UTM Content',
          path: 'reports/utm-content',
          order: 8
        },
        {
          id: 'reports-browser',
          title: 'Browser',
          path: 'reports/browser',
          order: 9
        },
        {
          id: 'reports-os',
          title: 'OS',
          path: 'reports/os',
          order: 10
        },
        {
          id: 'reports-device',
          title: 'Device',
          path: 'reports/device',
          order: 11
        }
      ]
    },
    {
      id: 'events',
      title: 'Events',
      order: 3,
      icon: 'ra-icons-marketing-arrow-chart-4',
      subMenuItems: [
        {
          id: 'forms',
          title: 'Forms',
          order: 1,
          path: 'conversions/forms'
        },
        {
          id: 'calls',
          title: 'Phone Calls',
          order: 2,
          path: 'conversions/phone-calls'
        },
        {
          id: 'opportunities',
          title: 'Opportunities',
          order: 3,
          path: 'opportunities'
        },
        {
          id: 'revenue',
          title: 'Revenue',
          order: 4,
          path: 'revenue'
        }
      ]
    },
    {
      id: 'insights',
      title: 'Insights',
      order: 4,
      icon: 'ra-icons-marketing-database-1',
      subMenuItems: [
        {
          id: 'paths',
          title: 'Paths',
          path: 'paths',
          order: 1
        },
        {
          id: 'conversion-influence',
          title: 'Conversion Influence',
          isPocOnly: true,
          path: 'insights/conversion-influence',
          order: 2
        }
      ]
    },
    {
      id: 'mmm',
      title: 'Marketing Mix Modelling',
      order: 5,
      icon: 'ra-icons-marketing-ai',
      isPocOnly: true,
      IsSubMenuOpen: false,
      isSiteProtectedFeature: true,
      subMenuItems: [
        {
          id: 'modelled-roi',
          title: 'Modelled ROI',
          path: 'marketing-mix-modelling/modelled-roi',
          order: 1
        },
        {
          id: 'budget-optimiser',
          title: 'Budget Optimiser',
          path: 'marketing-mix-modelling/budget-optimiser',
          order: 2
        },
        {
          id: 'budget-scenario-planner',
          title: 'Budget Scenario Planner',
          path: 'marketing-mix-modelling/budget-scenario-planner',
          order: 3
        },
        {
          id: 'campaign-budget-scenario-planner',
          title: 'Campaign Budget Scenario Planner',
          path: 'marketing-mix-modelling/campaign-budget-scenario-planner',
          order: 3
        }
      ]
    }
  ] as SideBarMenuItem[]
};

const sidebarSlice = createSlice({
  initialState,
  name: 'sidebarv2',
  reducers: {
    toggleSidebar: (state) => {
      state.isSidebarOpen = !state.isSidebarOpen;

      if (!state.isSidebarOpen) {
        state.sideBarMenuItems.forEach((menuItem) => {
          menuItem.isSubMenuOpen = false;
        });
      }
    },
    onToggleAccordion: (
      state,
      { payload: { menuItemId } }: PayloadAction<{ menuItemId: string }>
    ) => {
      state.isSidebarOpen = true;

      const menuItemToToggle = state.sideBarMenuItems.find(
        (menuItem) => menuItem.id === menuItemId
      );

      if (menuItemToToggle && menuItemToToggle.subMenuItems.length > 0) {
        menuItemToToggle.isSubMenuOpen = !menuItemToToggle.isSubMenuOpen;
      }
    },
    closeAllSideBarAccordions: (state) => {
      state.sideBarMenuItems.forEach((menuItem) => {
        menuItem.isSubMenuOpen = false;
      });
    }
  }
});

const selectSelf = (state: RootState) => state.sidebarv2;
const selectSideBarMenuItemsWithPocModeIsActiveAndSiteId = (
  state: RootState,
  isPocModeActive: boolean,
  siteId: string
) => ({ isPocModeActive, siteId });

export const selectIsSidebarOpen = createSelector(
  selectSelf,
  (state) => state.isSidebarOpen
);

export const selectSideBarMenuItems = createSelector(
  selectSelf,
  selectSideBarMenuItemsWithPocModeIsActiveAndSiteId,
  (state, { isPocModeActive, siteId }) => {
    return state.sideBarMenuItems.reduce(
      (accumulator: SideBarMenuItem[], currentValue) => {
        const whiteListedFeatureList = whiteListedFeatures.find(
          (feature) => feature.siteId === siteId
        );

        const filteredSubMenuItems = currentValue.subMenuItems.filter(
          (subMenuItem) => {
            if (subMenuItem.isPocOnly && !subMenuItem.isSiteProtectedFeature) {
              return isPocModeActive;
            } else if (subMenuItem.isSiteProtectedFeature) {
              return whiteListedFeatureList?.activatedProtectedFeatureIds.includes(
                subMenuItem.id
              );
            }

            return true;
          }
        );

        if (
          (!currentValue.isPocOnly || isPocModeActive) &&
          (!currentValue.isSiteProtectedFeature ||
            whiteListedFeatureList?.activatedProtectedFeatureIds.includes(
              currentValue.id
            ))
        ) {
          accumulator.push({
            ...currentValue,
            subMenuItems: filteredSubMenuItems
          });
        }

        return accumulator;
      },
      []
    );
  }
);

const selectSideBarMenuItemWithCurrentPathNameAndCurrentMenuItem = (
  state: RootState,
  currentPathName: string,
  currentMenuItem: SideBarMenuItem
) => {
  return { currentPathName, currentMenuItem };
};

export const selectIsRouteActive = createSelector(
  selectSelf,
  selectSideBarMenuItemWithCurrentPathNameAndCurrentMenuItem,
  (state, { currentPathName, currentMenuItem }): boolean => {
    const isRootPathActive =
      currentPathName === '' && currentMenuItem.path === 'dashboard';
    return (
      isRootPathActive ||
      currentMenuItem.path === currentPathName ||
      currentMenuItem.subMenuItems.some(
        (child: SideBarSubMenuItem) => child.path === currentPathName
      ) ||
      currentPathName.startsWith(currentMenuItem.path as string)
    );
  }
);

export const { toggleSidebar, onToggleAccordion, closeAllSideBarAccordions } =
  sidebarSlice.actions;

export default sidebarSlice.reducer;
